<template>
  <div v-if="isHydrating" class="flex-col flex items-center justify-center h-screen">
    <Loader />
  </div>
  <router-view v-else />
</template>

<script lang="ts">
import { Loader } from "@toruslabs/vue-components/Loader";
import { defineComponent } from "vue";

import { orgAnalytics, projectAnalytics } from "@/helpers/analytics";
import { clearSocialSpaSession } from "@/helpers/auth/spaClient";
import log from "@/helpers/log";
import { clearAuthStorage } from "@/store/modules/authStorage";
import { setAccessToken } from "@/utils/accessToken";
import { ROUTES } from "@/utils/enums";

import { teamModule, userModule } from "./store";

export default defineComponent({
  name: "App",
  components: { Loader },
  data: () => ({
    isHydrating: false,
  }),
  async created() {
    try {
      this.isHydrating = true;
      await this.rehydrate();
    } catch (error) {
      log.error("error while rehydrating", error);
    } finally {
      this.isHydrating = false;
    }
  },
  methods: {
    async clearStaleAuthSession(): Promise<void> {
      clearAuthStorage();
      setAccessToken(null);
      userModule.setUserInfo(undefined);
      await clearSocialSpaSession();
    },
    async rehydrate() {
      const route = this.$router.resolve({ path: window.location.pathname, query: Object.fromEntries(new URLSearchParams(window.location.search)) });
      const { query = {} } = route;

      // let agentic handle its own rehydration
      const isAgenticPath = route.path.startsWith("/agentic");
      if (isAgenticPath) {
        return;
      }

      // skip rehydration for invitation and accept-invite routes
      if (
        [ROUTES.INVITATION, ROUTES.ACCEPT_INVITE, ROUTES.START, ROUTES.END, ROUTES.AGENTIC_APPROVAL, ROUTES.LOGIN_EMAIL].includes(
          route.name as string
        )
      ) {
        return;
      }

      if (!(await userModule.rehydrateSession())) {
        this.$router.replace({ name: "login", query });
        return;
      }

      try {
        if (!(await userModule.authenticateWithDevDashboard())) {
          this.$router.replace({ name: "create-profile", query });
          return;
        }
      } catch (error) {
        const err = error as { code?: number };
        if (err.code === 401) {
          await this.clearStaleAuthSession();
          this.$router.replace({ name: "login", query });
          return;
        }
        throw error;
      }

      await userModule.getUserProfile();

      // analytics
      const { authConnection } = userModule.userInfoGetter || {};
      orgAnalytics.setGlobalProperties({
        user_id: userModule.userProfileGetter.ID,
        user_auth_connection: authConnection,
        user_type_of_login: userModule.typeOfLogin,
        user_mfa_enabled: false,
      });
      projectAnalytics.setGlobalProperties({
        user_id: userModule.userProfileGetter.ID,
        user_auth_connection: authConnection,
        user_type_of_login: userModule.typeOfLogin,
        user_mfa_enabled: false,
      });

      if (!userModule.userProfileGetter.accepted_terms_and_privacy_policy) {
        this.$router.push({ name: "update-profile", query });
        return;
      }

      if (query.invite_code) {
        this.$router.replace({ name: "accept-invite", query });
        return;
      }

      await teamModule.getTeams();

      if (teamModule.teamsGetter.length === 0) {
        this.$router.replace({ name: "create-organization" });
        return;
      }

      if (route.path === "/" || route.name === "login") {
        this.$router.replace({ name: "home" });
      }
    },
  },
});
</script>
